import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Typography,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Grid,
  TablePagination,
} from '@mui/material';
import { Edit, Delete, Add, Search, FilterList } from '@mui/icons-material';
import api from '../../services/api';

/**
 * Componente de Gestión de Buses con Búsqueda y Filtros
 * Path: backoffice-web/src/pages/Buses/Buses.js
 * Última modificación: 2025-12-30 22:30
 * Dev: cpalacios01@gmail.com | +595994648273
 */

export default function Buses() {
  const [buses, setBuses] = useState([]);
  const [filteredBuses, setFilteredBuses] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBus, setEditingBus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('todos');
  
  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    numero: '',
    patente: '',
    marca: '',
    modelo: '',
    anio: '',
    capacidad: '',
    estado: 'activo',
  });

  // Cargar buses al iniciar
  useEffect(() => {
    fetchBuses();
  }, []);

  // Aplicar filtros cuando cambian los datos o los filtros
  useEffect(() => {
    applyFilters();
  }, [buses, searchTerm, estadoFilter]);

  const fetchBuses = async () => {
    try {
      const response = await api.get('/buses');
      setBuses(response.data);
    } catch (error) {
      console.error('Error al cargar buses:', error);
      setError('Error al cargar los buses');
    }
  };

  const applyFilters = () => {
    let filtered = [...buses];

    // Filtro de búsqueda
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(bus => 
        (bus.numero?.toLowerCase() || '').includes(search) ||
        (bus.patente?.toLowerCase() || '').includes(search) ||
        (bus.marca?.toLowerCase() || '').includes(search) ||
        (bus.modelo?.toLowerCase() || '').includes(search)
      );
    }

    // Filtro de estado
    if (estadoFilter !== 'todos') {
      filtered = filtered.filter(bus => bus.estado === estadoFilter);
    }

    setFilteredBuses(filtered);
    setPage(0); // Resetear a primera página al filtrar
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleEstadoFilterChange = (e) => {
    setEstadoFilter(e.target.value);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setEstadoFilter('todos');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (bus = null) => {
    if (bus) {
      setEditingBus(bus);
      setFormData({
        numero: bus.numero || '',
        patente: bus.patente || '',
        marca: bus.marca || '',
        modelo: bus.modelo || '',
        anio: bus.anio || '',
        capacidad: bus.capacidad || '',
        estado: bus.estado || 'activo',
      });
    } else {
      setEditingBus(null);
      setFormData({
        numero: '',
        patente: '',
        marca: '',
        modelo: '',
        anio: '',
        capacidad: '',
        estado: 'activo',
      });
    }
    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBus(null);
    setFormData({
      numero: '',
      patente: '',
      marca: '',
      modelo: '',
      anio: '',
      capacidad: '',
      estado: 'activo',
    });
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Preparar datos para enviar
      const dataToSend = {
        numero: formData.numero,
        patente: formData.patente,
        marca: formData.marca || null,
        modelo: formData.modelo || null,
        anio: formData.anio ? parseInt(formData.anio) : null,
        capacidad: formData.capacidad ? parseInt(formData.capacidad) : null,
        estado: formData.estado,
      };

      if (editingBus) {
        // Actualizar bus existente
        await api.put(`/buses/${editingBus.id}`, dataToSend);
        setSuccess('Bus actualizado exitosamente');
      } else {
        // Crear nuevo bus
        await api.post('/buses', dataToSend);
        setSuccess('Bus creado exitosamente');
      }

      // Recargar lista y cerrar diálogo
      await fetchBuses();
      setTimeout(() => {
        handleCloseDialog();
      }, 1500);

    } catch (error) {
      console.error('Error al guardar bus:', error);
      
      if (error.response?.status === 422) {
        // Error de validación
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat().join(', ');
        setError(`Error de validación: ${errorMessages}`);
      } else {
        setError(error.response?.data?.message || 'Error al guardar el bus');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este bus?')) {
      return;
    }

    try {
      await api.delete(`/buses/${id}`);
      setSuccess('Bus eliminado exitosamente');
      fetchBuses();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error al eliminar bus:', error);
      setError(error.response?.data?.message || 'Error al eliminar el bus');
      setTimeout(() => setError(''), 3000);
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'activo':
        return 'success';
      case 'mantenimiento':
        return 'warning';
      case 'inactivo':
        return 'error';
      default:
        return 'default';
    }
  };

  // Calcular datos paginados
  const paginatedBuses = filteredBuses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Gestión de Buses</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Bus
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Filtros y búsqueda */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Buscar por número, patente, marca o modelo..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={estadoFilter}
                label="Estado"
                onChange={handleEstadoFilterChange}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterList />
                  </InputAdornment>
                }
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="activo">Activo</MenuItem>
                <MenuItem value="mantenimiento">Mantenimiento</MenuItem>
                <MenuItem value="inactivo">Inactivo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleClearFilters}
            >
              Limpiar Filtros
            </Button>
          </Grid>
        </Grid>
        
        {/* Contador de resultados */}
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Mostrando {filteredBuses.length} de {buses.length} buses
          </Typography>
        </Box>
      </Paper>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Número</TableCell>
              <TableCell>Patente</TableCell>
              <TableCell>Marca</TableCell>
              <TableCell>Modelo</TableCell>
              <TableCell>Año</TableCell>
              <TableCell>Capacidad</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedBuses.map((bus) => (
              <TableRow key={bus.id}>
                <TableCell>{bus.id}</TableCell>
                <TableCell>{bus.numero}</TableCell>
                <TableCell>{bus.patente}</TableCell>
                <TableCell>{bus.marca || '-'}</TableCell>
                <TableCell>{bus.modelo || '-'}</TableCell>
                <TableCell>{bus.anio || '-'}</TableCell>
                <TableCell>{bus.capacidad || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={bus.estado}
                    color={getEstadoColor(bus.estado)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(bus)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(bus.id)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedBuses.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {searchTerm || estadoFilter !== 'todos' 
                    ? 'No se encontraron buses con los filtros aplicados'
                    : 'No hay buses registrados'
                  }
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        
        {/* Paginación */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredBuses.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </TableContainer>

      {/* Diálogo de crear/editar */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBus ? 'Editar Bus' : 'Nuevo Bus'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Box component="form" sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Número de Bus *"
              name="numero"
              value={formData.numero}
              onChange={handleChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Patente *"
              name="patente"
              value={formData.patente}
              onChange={handleChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Marca"
              name="marca"
              value={formData.marca}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Modelo"
              name="modelo"
              value={formData.modelo}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Año"
              name="anio"
              type="number"
              value={formData.anio}
              onChange={handleChange}
              margin="normal"
              inputProps={{ min: 1900, max: 2100 }}
            />
            <TextField
              fullWidth
              label="Capacidad"
              name="capacidad"
              type="number"
              value={formData.capacidad}
              onChange={handleChange}
              margin="normal"
              inputProps={{ min: 1 }}
            />
            <TextField
              fullWidth
              select
              label="Estado"
              name="estado"
              value={formData.estado}
              onChange={handleChange}
              margin="normal"
            >
              <MenuItem value="activo">Activo</MenuItem>
              <MenuItem value="mantenimiento">Mantenimiento</MenuItem>
              <MenuItem value="inactivo">Inactivo</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.numero || !formData.patente}
          >
            {loading ? 'Guardando...' : editingBus ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// cpalacios01@gmail.com | +595994648273